import React, { useCallback, useRef, useState } from "react";
import { SPREADSHEET_EXTENSIONS, validateSpreadsheetFile } from "../parse-spreadsheet";

const ACCEPT = SPREADSHEET_EXTENSIONS.join(",");

const SpreadsheetDropzone = ({
  onFile,
  disabled,
  error,
  busy,
  variant = "drop",
  buttonLabel = "Upload a different file",
}) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [localError, setLocalError] = useState(null);

  const handleFiles = useCallback(
    (file) => {
      const validation = validateSpreadsheetFile(file);
      if (!validation.ok) {
        setLocalError(validation.error);
        return;
      }
      setLocalError(null);
      onFile(file);
    },
    [onFile],
  );

  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      accept={ACCEPT}
      hidden
      disabled={disabled}
      onChange={(event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (file) handleFiles(file);
      }}
    />
  );

  const message = error || localError;
  const openPicker = () => {
    if (!disabled) inputRef.current?.click();
  };

  if (variant === "button") {
    return (
      <div className="start-replace">
        {fileInput}
        <button type="button" className="start-btn start-btn--solid" onClick={openPicker} disabled={disabled}>
          {busy ? "Reading spreadsheet…" : buttonLabel}
        </button>
        {message && (
          <p className="start-error" role="alert">
            {message}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="start-drop-wrap">
      <div
        className={`start-drop${dragOver ? " start-drop--over" : ""}${disabled ? " start-drop--disabled" : ""}`}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragOver(false);
          if (disabled) return;
          const file = event.dataTransfer?.files?.[0];
          if (file) handleFiles(file);
        }}
        onClick={openPicker}
        onKeyDown={(event) => {
          if (disabled) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openPicker();
          }
        }}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label="Upload a student spreadsheet"
        aria-disabled={disabled}
      >
        {fileInput}
        <span className="start-drop__glow" aria-hidden="true" />
        <span className="start-drop__icon" aria-hidden="true">
          <svg viewBox="0 0 64 64" width="52" height="52">
            <rect x="10" y="8" width="34" height="44" rx="4" fill="none" stroke="currentColor" strokeWidth="2" />
            <path d="M16 20h22M16 28h22M16 36h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <circle cx="46" cy="46" r="12" fill="#061018" stroke="currentColor" strokeWidth="2" />
            <path d="M46 40v12M40 46h12" stroke="#5eead4" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </span>
        <strong>{busy ? "Reading spreadsheet…" : "Drop your spreadsheet here"}</strong>
        <span>Excel or CSV · we interpret whatever columns you have</span>
      </div>
      {message && (
        <p className="start-error" role="alert">
          {message}
        </p>
      )}
    </div>
  );
};

export default SpreadsheetDropzone;
